import {internalApi} from "../../common/axios";
import {buildUrl} from "../../shared/utils/net";
import {useMutationBase, useQueryBase} from "../../shared/hooks/useHookBase";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import toast from "react-hot-toast";
import {useEffect} from "react";

const fetchPlayerShoppingRoadData = async (params) => {
    const {data} = await internalApi.get(buildUrl('/player/shoppingRoad', params));
    return data.shoppingRoadInfo;
}

export const useGetPlayerShoppingRoadData = ({ userKey, shoppingRoadSeq }) => {
    const enabled = !!shoppingRoadSeq;
    const query = useQueryBase(
        ['playerShoppingRoad', { userKey, shoppingRoadSeq }],
        () => fetchPlayerShoppingRoadData({ userKey, shoppingRoadSeq }),
        { enabled }
    );

    useEffect(() => {
        if (!shoppingRoadSeq) {
            toast.error("쇼핑로드가 활성화 되어있지 않습니다.");
        }
    }, [shoppingRoadSeq]);

    return query;
};

const setShoppingRoadRestart = async (body) => {
    await internalApi.post('/player/shoppingRoad/restart', body);
    toast.success("쇼핑로드가 재시작되었습니다.");
}

export const useSetShoppingRoadRestart = (body) => {
    return useMutationBase(['playerShoppingRoad', body], (body) => setShoppingRoadRestart(body));
}

const setShoppingRoadRestore = async (body) => {
    await internalApi.post('/player/shoppingRoad/restore', body);
    toast.success("쇼핑로드가 복구되었습니다.");
}

export const useSetShoppingRoadRestore = ( body ) => {
    return useMutationBase(['playerShoppingRoad', { userKey: body.userKey, shoppingRoadSeq: body.shoppingRoadSeq }], (body) => setShoppingRoadRestore(body));
}

const fetchPlayerTrophyInfo = async (params) => {
    const {data} = await internalApi.get(buildUrl('/player/trophy', params));
    return data.rows;
}

export const useGetPlayerTrophyInfo = ({ userKey }) => {
    return useQueryBase(['playerTrophy', { userKey }], () => fetchPlayerTrophyInfo({ userKey }));
};

const fetchPlayerTrophyMissionInfo = async (params) => {
    const {data} = await internalApi.get(buildUrl('/player/trophy/mission', params));
    return data.rows;
}

export const useGetPlayerTrophyMissionInfo = ({ userKey, trophyId }) => {
    const enabled = !!trophyId;
    return useQueryBase(['playerTrophyMission', { userKey, trophyId }], () => fetchPlayerTrophyMissionInfo({ userKey, trophyId }), { enabled });
};

const fetchTrophyMissionGroup = async (params) => {
    const {data} = await internalApi.get(buildUrl('/player/trophy/missionGroup', params));
    return data.missions;
}

export const useGetTrophyMissionGroup = (gIdx) => {
    const enabled = !!gIdx;
    return useQueryBase(['trophyMissionGroup', { gIdx }], () => fetchTrophyMissionGroup({ gIdx }), { enabled });
};

const setTrophyMissionManual = async (body) => {
    await internalApi.post('/player/trophy/manual', body);
    toast.success("수동처리 보상 우편이 발송되었습니다.");
}

export const useSetTrophyMissionManual = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: setTrophyMissionManual,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['playerTrophyMission']});
            queryClient.invalidateQueries({queryKey: ['playerTrophy']});
        },
    });
};

const setTrophyFinalReward = async (body) => {
    await internalApi.post('/player/trophy/finalReward', body);
    toast.success("최종보상 우편이 발송되었습니다.");
}

export const useSetTrophyFinalReward = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: setTrophyFinalReward,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['playerTrophy']});
        },
    });
};

const fetchPlayerPartnerLog = async (params) => {
    const {data} = await internalApi.get(buildUrl('/logs/partner', params));
    return data.rows;
}

export const useGetPlayerPartnerLog = ({pid, eventType, begin, end}) => {
    const enabled = !!pid && !!eventType;
    return useQueryBase(
        ['playerPartnerLog', {pid, eventType, begin, end}],
        () => fetchPlayerPartnerLog({pid, eventType, begin, end, filters: '{}'}),
        {enabled}
    );
};

const fetchPartnerData = async (params) => {
    const {data} = await internalApi.get(buildUrl('/player/partner', params));
    return data;
};

export const useGetPartnerData = ({userKey}) => {
    return useQueryBase(['playerPartner', {userKey}], () => fetchPartnerData({userKey}));
};

const putPartnerState = async (body) => {
    await internalApi.put('/player/partner/state', body);
    toast.success("파트너 상태가 수정되었습니다.");
};

export const useSetPartnerState = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: putPartnerState,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: ['playerPartner', {userKey: variables.userKey}]});
        },
    });
};

const putPartnerBond = async (body) => {
    await internalApi.put('/player/partner/bond', body);
    toast.success("파트너 정보가 수정되었습니다.");
};

export const useSetPartnerBond = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: putPartnerBond,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: ['playerPartner', {userKey: variables.userKey}]});
        },
    });
};

const putPartnerInventory = async (body) => {
    await internalApi.put('/player/partner/inventory', body);
    toast.success("인벤토리가 수정되었습니다.");
};

export const useSetPartnerInventory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: putPartnerInventory,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: ['playerPartner', {userKey: variables.userKey}]});
        },
    });
};

const putPartnerSkillUsedAt = async (body) => {
    await internalApi.put('/player/partner/skill', body);
    toast.success("스킬 사용 시간이 수정되었습니다.");
};

export const useSetPartnerSkillUsedAt = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: putPartnerSkillUsedAt,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: ['playerPartner', {userKey: variables.userKey}]});
        },
    });
};

const postAddPartnerSkill = async (body) => {
    await internalApi.post('/player/partner/skill', body);
    toast.success("스킬이 추가되었습니다.");
};

export const useAddPartnerSkill = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: postAddPartnerSkill,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: ['playerPartner', {userKey: variables.userKey}]});
        },
    });
};

const deletePartnerCostume = async ({userKey, costumeId}) => {
    await internalApi.delete(buildUrl('/player/partner/costume', {userKey, costumeId}));
    toast.success("코스튬이 삭제되었습니다.");
};

export const useDeletePartnerCostume = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deletePartnerCostume,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: ['playerPartner', {userKey: variables.userKey}]});
        },
    });
};

const fetchProsperityInfo = async (params) => {
    const {data} = await internalApi.get(buildUrl('/player/prosperity', params));
    return data;
};

export const useGetProsperityInfo = ({userKey}) => {
    return useQueryBase(['playerProsperity', {userKey}], () => fetchProsperityInfo({userKey}));
};

const postResendProsperityReward = async (body) => {
    await internalApi.post('/player/prosperity/resend', body);
    toast.success("번영도 보상이 재지급되었습니다.");
};

export const useResendProsperityReward = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: postResendProsperityReward,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: ['playerProsperity', {userKey: variables.userKey}]});
        },
    });
};

const fetchTwinRouletteInfo = async (params) => {
    const {data} = await internalApi.get(buildUrl('/player/twinRoulette', params));
    return data;
};

export const useGetTwinRouletteInfo = ({userKey}) => {
    return useQueryBase(['playerTwinRoulette', {userKey}], () => fetchTwinRouletteInfo({userKey}));
};

const useTwinRouletteMutation = (mutationFn) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({queryKey: ['playerTwinRoulette', {userKey: variables.userKey}]});
        },
    });
};

export const useSetTwinRouletteCurrency = () => useTwinRouletteMutation(async (body) => {
    await internalApi.put('/player/twinRoulette/currency', body);
    toast.success("트윈 룰렛 재화가 수정되었습니다.");
});

export const useActivateTwinRoulettePaid = () => useTwinRouletteMutation(async (body) => {
    await internalApi.post('/player/twinRoulette/activatePaid', body);
    toast.success("유료 보상이 활성화되었습니다.");
});
